import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import { IconCashBanknote, IconTrendingUp, IconTrendingDown, IconScale } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import AnimatedStatCard from '../../../../../ui-component/extended/AnimatedStatCard';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDateBR = (value) => {
    if (!value) return '-';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

const CashFlow = () => {
    const [appId, setAppId] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [data, setData] = useState({ transactions: [], total_entradas: 0, total_saidas: 0, saldo: 0 });

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        carregarFluxo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const carregarFluxo = async (customStart, customEnd) => {
        const params = new URLSearchParams();
        if (customStart || startDate) params.set('start_date', customStart || startDate);
        if (customEnd || endDate) params.set('end_date', customEnd || endDate);
        const res = await api.get(`v1/apps/${appId}/financial/cashflow/?${params.toString()}`);
        setData(res.data);
        setStartDate(res.data.start_date);
        setEndDate(res.data.end_date);
    };

    const handleFilter = () => {
        carregarFluxo(startDate, endDate);
    };

    return (
        <Box>
            <PageHeader
                icon={IconCashBanknote}
                gradient="teal"
                title="Caixa"
                subtitle="Fluxo de contas já pagas e recebidas no período"
                actions={
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="De"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <TextField
                            label="Até"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                        <Chip label="Filtrar" color="primary" onClick={handleFilter} sx={{ cursor: 'pointer', px: 1 }} />
                    </Stack>
                }
            />

            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                    <AnimatedStatCard index={0} icon={IconTrendingUp} gradient="teal" label="Entradas" value={formatCurrencyBR(data.total_entradas)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <AnimatedStatCard index={1} icon={IconTrendingDown} gradient="coral" label="Saídas" value={formatCurrencyBR(data.total_saidas)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <AnimatedStatCard index={2} icon={IconScale} gradient="blue" label="Saldo do período" value={formatCurrencyBR(data.saldo)} />
                </Grid>
            </Grid>

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Data</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Organização</TableCell>
                                <TableCell align="right">Valor</TableCell>
                                <TableCell align="right">Saldo</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.transactions.map((t, index) => (
                                <TableRow key={index}>
                                    <TableCell>{formatDateBR(t.date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={t.type === 'entrada' ? 'Entrada' : 'Saída'}
                                            color={t.type === 'entrada' ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{t.description}</TableCell>
                                    <TableCell>{t.organization_name}</TableCell>
                                    <TableCell align="right" sx={{ color: t.type === 'entrada' ? 'success.main' : 'error.main' }}>
                                        {t.type === 'entrada' ? '+' : '-'} {formatCurrencyBR(t.amount)}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrencyBR(t.balance)}</TableCell>
                                </TableRow>
                            ))}
                            {data.transactions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Nenhuma movimentação no período
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CashFlow;
